<script setup lang="ts">
import { computed } from 'vue';

const props = withDefaults(
    defineProps<{
        label?: string
        required?: boolean
        tag?: 'label' | 'div'
        errors?: string | string[] | null
        /**
         * Angular's `kgr-form-field` sets `is-invalid` from the control's validity, not from the
         * message list — a validator without a message (`Validators.email`) marks the field red
         * without printing anything. Set this to reproduce that.
         */
        invalid?: boolean
        helpText?: string
        maxlength?: number
        modelValue?: string
        /** Angular's `kgr-form-field` constrains the field to 400px unless told otherwise. */
        constrainedWidth?: boolean
        /**
         * Angular's `kgr-form-field` carries no `kgr-row` — the vertical rhythm comes from the
         * `.kgr-inputGroup + .kgr-inputGroup` sibling margin alone. Existing call sites rely on the
         * extra row margin, so it stays the default and is opted out of where Angular has none.
         */
        row?: boolean
    }>(),
    {
        tag: 'label',
        required: false,
        constrainedWidth: true,
        row: true,
    },
);

const errorList = computed((): string[] => {
    if (!props.errors) {
        return [];
    }
    return Array.isArray(props.errors) ? props.errors.filter(Boolean) : [props.errors];
});

const hasErrors = computed(() => errorList.value.length > 0 || props.invalid === true);

const remainingChars = computed(() => (props.maxlength !== undefined ? props.maxlength - (props.modelValue?.length ?? 0) : undefined));
</script>

<template>
    <component
        :is="tag"
        class="kgr-inputGroup"
        :class="{ 'kgr-row': props.row, 'is-invalid': hasErrors, 'kgr-inputGroup--constrainedWidth': props.constrainedWidth }"
    >
        <span
            v-if="label"
            class="kgr-inputGroup__label"
        >{{ label }}{{ required ? ' *' : '' }}</span>

        <div
            v-for="error in errorList"
            :key="error"
            class="kgr-inputGroup__text kgr-inputGroup__text--error"
        >
            {{ error }}
        </div>

        <div
            v-if="helpText"
            class="kgr-inputGroup__text"
        >
            {{ helpText }}
        </div>

        <div
            v-if="maxlength !== undefined"
            class="kgr-inputGroup__text"
        >
            {{ $t('Max. {n} Zeichen inkl. Leerzeichen.', { n: maxlength }) }}
        </div>

        <div
            v-if="maxlength !== undefined"
            class="kgr-inputGroup__text"
        >
            <!-- Angular bolds „<n> Zeichen" as one unit, Vue only the number. Not changed here: the
                 keypath below is the registered catalogue key (frontend.*.xlf), and moving the word
                 into the placeholder would need a new unit in all eleven locale files. -->
            <i18n-t
                keypath="Noch {n} Zeichen verfügbar."
                tag="span"
            >
                <template #n>
                    <span
                        class="kgr-text kgr-text--bold kgr-color"
                        :class="{ 'kgr-color--error': remainingChars !== undefined && remainingChars < 0 }"
                    >{{ remainingChars }}</span>
                </template>
            </i18n-t>
        </div>

        <slot></slot>
    </component>
</template>
